import React, { useCallback, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PiCaretDown } from 'react-icons/pi'
import { useLeftNavStore } from './left-nav-store'
import { SlideOutNavButton } from './SlideOutNavButton'
import { FormattedLabel } from '../shared'
import { cn } from 'components/UI/cn'
import type { NavSection } from './types'

export interface NavGroupItem {
  label: string
  path: string
  description?: string
}

interface SlideOutNavGroupProps {
  label: string
  description?: string
  items: NavGroupItem[]
  section?: NavSection
  defaultExpanded?: boolean
  isActiveGroup?: boolean
}

export const SlideOutNavGroup: React.FC<SlideOutNavGroupProps> = ({
  label,
  description,
  items,
  section,
  defaultExpanded = false,
  isActiveGroup = false,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { setLastVisited, closeSlideOut } = useLeftNavStore()
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || isActiveGroup)

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  const handleItemClick = useCallback(
    (item: NavGroupItem) => {
      navigate(item.path)
      if (section) {
        setLastVisited(section, item.path)
      }
      closeSlideOut()
    },
    [navigate, section, setLastVisited, closeSlideOut]
  )

  // Hover is handled via CSS in main.css ([data-slide-out-panel] button:hover)
  return (
    <div className="m-0 p-0">
      {/* Header */}
      <button
        onClick={toggleExpanded}
        className="flex items-center justify-between w-full h-9 pl-5 pr-2 border-none bg-transparent rounded-lg text-left cursor-pointer transition-colors duration-150"
      >
        <span
          className={cn(
            'text-[14px] text-[#191d23] leading-[1.4]',
            isExpanded ? 'font-bold' : 'font-medium'
          )}
        >
          {label}
          {description && (
            <span className="text-xs font-light italic text-[#6e6e6e] leading-[1.4] ml-1">
              {description}
            </span>
          )}
        </span>
        <PiCaretDown
          size={16}
          className={cn(
            'text-[#07311e] transition-transform duration-200',
            isExpanded ? 'rotate-180' : 'rotate-0'
          )}
        />
      </button>

      {/* Items */}
      <div
        className={cn('flex-col gap-1.5 pl-3', isExpanded ? 'flex' : 'hidden')}
      >
        {items.map((item) => {
          const isActive = location.pathname.startsWith(item.path)
          return (
            <SlideOutNavButton
              key={item.path}
              onClick={() => handleItemClick(item)}
              isActive={isActive}
              aria-current={isActive ? 'page' : undefined}
            >
              <FormattedLabel
                label={item.label}
                variant="nav-item"
                isActive={isActive}
              />
            </SlideOutNavButton>
          )
        })}
      </div>
    </div>
  )
}
